<template>
  <div>
    <v-menu
      v-if="user && showACtionAvatar"
      :class="specificity ? 'prioritize-specificity' : ''"
      rounded="lg"
      bottom
      left
      offset-y
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          text
          @click="$emit('toggle-menu-mb')"
          color="primary"
          class="d-flex align-center not-regular-btn fixed-size"
          :class="[{ 'px-2': !xsMobile }, { 'pa-0': xsMobile }]"
          v-bind="attrs"
          v-on="on"
        >
          <v-avatar
            v-if="authUser"
            :size="xsMobile ? '34' : '42'"
            color="primary"
            style="margin-right: -12px"
            :class="{ 'mr-2': !xsMobile }"
          >
            <avatar-editor v-if="authUserAvatarSettings" :key="authUserPhoto.options.originalPhoto" class="editor" :imageUrl="authUserPhoto.options.originalPhoto" :settings.sync="authUserAvatarSettings" read-only />
            <v-img v-else-if="authUser.avatar && authUser.avatar.trim() != ''" :src="authUser.avatar"></v-img>
            <span v-else class="white--text text-body" v-text="authInitials"></span>
          </v-avatar>
          <v-avatar :size="xsMobile ? '34' : '42'" color="primary" :class="{ 'mr-2': !xsMobile }">
            <avatar-editor v-if="userAvatarSettings" :key="userPhoto.options.originalPhoto" class="editor" :imageUrl="userPhoto.options.originalPhoto" :settings.sync="userAvatarSettings" read-only />
            <v-img v-else-if="user.avatar && user.avatar.trim() != ''" :src="user.avatar"></v-img>
            <span v-else class="white--text text-body" v-text="initials"></span>
          </v-avatar>
          <div v-if="!xsMobile" class="text-nowrap">{{ user.resolvedDisplayName }}</div>
        </v-btn>
      </template>

      <v-list class="menu-item" v-if="!xsMobile">
        <v-list-item-group color="primary">
          <v-list-item
            v-for="item in items"
            :key="item.id"
            :id="item.id"
            :to="item.route"
            @click="onMenuItemSelected(item)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ $t(`appBar.userMenu.${item.id}`) }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-menu>
    <v-navigation-drawer
      v-if="user && xsMobile"
      class="menu-item"
      :class="specificity ? 'prioritize-specificity' : ''"
      right
      clipped
      app
      v-model="isMenuOpen"
      disable-route-watcher
      disable-resize-watcher
    >
      <template v-slot:prepend>
        <v-list-item id="userMenu" class="flex text-center" link :to="{ name: 'Account' }">
          <v-list-item-content>
            <v-list-item-title>
              <v-avatar v-if="authUser" size="42" color="primary" style="margin-right: -12px" xclass="mr-2">
                <avatar-editor v-if="authUserAvatarSettings" :key="authUserPhoto.options.originalPhoto" class="editor" :imageUrl="authUserPhoto.options.originalPhoto" :settings.sync="authUserAvatarSettings" read-only />
                <v-img v-else-if="authUser.avatar && authUser.avatar.trim() != ''" :src="authUser.avatar"></v-img>
                <span v-else class="white--text text-body" v-text="authInitials"></span>
              </v-avatar>
              <v-avatar size="42" color="primary" class="mr-2">
                <avatar-editor v-if="userAvatarSettings" :key="userPhoto.options.originalPhoto" class="editor" :imageUrl="userPhoto.options.originalPhoto" :settings.sync="userAvatarSettings" read-only />
                <v-img v-else-if="user.avatar && user.avatar.trim() != ''" :src="user.avatar"></v-img>
                <span v-else class="white--text text-body" v-text="initials"></span>
              </v-avatar>
            </v-list-item-title>
            <v-list-item-subtitle color="primary" class="mt-2 primary--text">
              {{ user.resolvedDisplayName }}
            </v-list-item-subtitle
            >
          </v-list-item-content>
        </v-list-item>
      </template>
      <v-divider></v-divider>
      <v-list>
        <v-list-item-group color="primary">
          <v-list-item
            v-for="item in items"
            :key="item.id"
            :id="item.id"
            :to="item.route"
            @click="onMenuItemSelected(item)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ $t(`appBar.userMenu.${item.id}`) }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>
    <!-- Contact Us Modal -->
    <v-dialog v-model="showContactUsModal" max-width="500" @keydown.esc="showContactUsModal = false">
      <v-card>
        <v-card-title class="primary--text">Contact Us</v-card-title>
        <v-card-text>
          <v-textarea v-model="emailBody" label="Enter your message" outlined rows="5" class="mt-3"></v-textarea>
          <div class="mt-3">
            By clicking submit, an email will be sent to
            <strong class="primary--text mb-1">{{ $i18n.t("fields.supportOnviv") }}</strong
            >.
            <!-- TODO: Remove After February Product Showcase -->
            <v-icon v-if="!isProd" color="primary" class="mr-6">fa-brands fa-jedi-order</v-icon>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showContactUsModal = false">Cancel</v-btn>
          <v-btn :loading="loading" :disabled="loading" text color="primary" @click="sendEmail">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { UserType } from "@/config";

import { createHelpers } from "vuex-typescript-interface";
import { RootStoreInterface } from "@/store/types";
import { SupportService, UserPartialItem, UserPhotoItem, UserService } from "@/service";
import { markImpersonationSwitch } from "@/main";
import AvatarEditor, { AvatarSettings } from "@/components/objects/AvatarEditor.vue";

const AVATAR_ICON_SIZE = 42;

const { mapGetters, mapActions } = createHelpers<RootStoreInterface>();

const getters = mapGetters(["isLoggedIn", "user", "authUser", "userCanPost"]);
const actions = mapActions(["logout", "cancelLoginAs"]);

type MenuItem = {
  title: string;
  subtitle?: string;
  id: string;
  route?: {
    name: string;
    path?: string;
    hash?: string;
  };
};

export default Vue.extend({
  name: "UserMenu",
  components: {
    AvatarEditor
  },
  data() {
    return {
      showContactUsModal: false,
      emailBody: "",
      loading: false,
      userPhoto: {} as UserPhotoItem,
      authUserPhoto: {} as UserPhotoItem
    };
  },
  props: {
    // State of the drawer/menu button
    open: {
      type: Boolean,
      default: false
    },
    specificity: {
      type: Boolean,
      default: false
    },
    showACtionAvatar: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...getters,
    isProd(): boolean {
      return this.$store.getters.currentEnvironment === "prod";
    },
    userAvatarSettings: {
      get(): AvatarSettings | null {
        return this.buildAvatarSettings(this.userPhoto);
      },
      set() {  }
    },
    authUserAvatarSettings: {
      get(): AvatarSettings | null {
        return this.buildAvatarSettings(this.authUserPhoto);
      },
      set() { }
    },
    isMenuOpen: {
      get(): boolean {
        return this.open;
      },
      set(newValue: boolean) {
        this.$emit("update:open", newValue);
      }
    },

    isCsr(): boolean {
      return this.isLoggedIn && this.user?.userType == UserType.CSR;
    },
    isExpert(): boolean {
      return this.isLoggedIn && this.user?.userType == UserType.EXPERT;
    },
    isPlanManager(): boolean {
      return this.isLoggedIn && this.user?.userType === UserType.PLAN_MANAGER;
    },
    taxFormNotStarted(): boolean {
      return (
        this.user?.taxFormStatus == UserPartialItem.taxFormStatus.NOT_STARTED ||
        this.user?.taxFormStatus == UserPartialItem.taxFormStatus.IN_PROGRESS
      );
    },
    initials(): string {
      return (
        (this.user?.firstName?.substring(0, 1).toUpperCase() ?? "") +
        (this.user?.lastName?.substring(0, 1).toUpperCase() ?? "?")
      );
    },
    authInitials(): string {
      return (
        (this.authUser?.firstName?.substring(0, 1).toUpperCase() ?? "") +
        (this.authUser?.lastName?.substring(0, 1).toUpperCase() ?? "?")
      );
    },
    tenantSelectionPending(): boolean {
      return this.$route.name === "ChangeTenant";
    },
    items(): Array<MenuItem> {
      let mlist = [];
      if (this.isLoggedIn && !this.isCsr && this.userCanPost) {
        mlist.push({ title: "Start a Discussion", id: "askQuestion", route: { name: "NewQuery" } });
      }
      mlist.push({ title: "Contact Us", id: "contactUs" });
      if (!this.tenantSelectionPending) {
        mlist.push({
          title: "Dashbooard",
          id: "dashboard",
          route: { name: this.dashboardRouteName, path: this.dashboardRouteName == "Home" ? "/" : undefined }
        });
      }
      if (this.taxFormNotStarted && this.isExpert && !this.isCsr) {
        mlist.push({ title: "Fill Out Tax Forms", id: "taxForms" });
      }
      if (this.isLoggedIn && ![1, 3].includes(this.user?.userType ?? 1)) {
        mlist.push({ title: "My Discussions", id: "discussions", route: { name: "Discussions" } });
      }
      if (this.isPlanManager) {
        mlist.push({ title: "My Learners", id: "myLearners" });
      }
      if (!this.tenantSelectionPending) {
        mlist.push({ title: "User Profile", id: "profile", route: { name: "Account" } });
      }      if (this.isLoggedIn && this.authUser) {
        mlist.push({
          title: "Return to your account",
          subtitle: this.authUser.displayName || (this.authUser as any).name || "CSR",
          id: "cancelLoginAs"
        });
      }
      mlist.push({ title: "Sign Out", id: "signout" });
      //move "Contact Us" to the second position except for various scenarios
      // Position "Contact Us" after My Learners (or My Discussions), otherwise fallback
      const contactIndex = mlist.findIndex((item) => item.id === "contactUs");
      if (contactIndex !== -1) {
        const contactUsItem = mlist.splice(contactIndex, 1)[0];
        const myLearnersIndex = mlist.findIndex((item) => item.id === "myLearners");
        const discussionsIndex = mlist.findIndex((item) => item.id === "discussions");
        let insertIndex: number;
        if (myLearnersIndex !== -1) {
          insertIndex = myLearnersIndex + 1;
        } else if (discussionsIndex !== -1) {
          insertIndex = discussionsIndex + 1;
        } else if (this.taxFormNotStarted) {
          insertIndex = 2;
        } else {
          insertIndex = 1;
        }
        mlist.splice(insertIndex, 0, contactUsItem);
      }
      return mlist;
    },
    dashboardRouteName(): string {
      if (this.isLoggedIn && this.user && this.user.userType) {
        switch (this.user.userType) {
          case 1:
            return "MemberLobby"; // "/member/lobby"
          case 3:
            return "QueryIndex"; // "/admin/queries"
          default:
            return "Home"; // "/"
        }
      }
      return "Home"; // "/"
    },
    isExpert(): boolean {
      return this.user?.noeticExpert ?? false;
    },
    xsMobile(): boolean {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  methods: {
    ...actions,
    buildAvatarSettings(photo: UserPhotoItem): AvatarSettings | null {
      const options = photo.options;
      if (!options || !options.originalPhoto) return null;
      // The backend returns x/y relative to the real uploaded image's pixel
      // dimensions (original_width/original_height), not the frontend's
      // crop canvas size, so scale against those.
      const savedImageWidth = options.original_width || 200;
      const savedImageHeight = options.original_height || 200;
      const scaleX = AVATAR_ICON_SIZE / savedImageWidth;
      const scaleY = AVATAR_ICON_SIZE / savedImageHeight;
      return {
        x: (options.x ?? 0) * scaleX,
        y: (options.y ?? 0) * scaleY,
        width: (options.original_width ?? 0) * scaleX,
        height: (options.original_height ?? 0) * scaleY,
        canvasWidth: AVATAR_ICON_SIZE,
        canvasHeight: AVATAR_ICON_SIZE,
        radius: AVATAR_ICON_SIZE / 2,
        angle: options.angle ?? 0,
        scale: options.scale ?? 1
      };
    },
    async loadUserPhoto() {
      if (!this.user?.id || !this.user.avatar) {
        this.userPhoto = {};
        return;
      }
      try {
        this.userPhoto = await UserService.getUserPhotoById({ id: Number(this.user.id) });
      } catch (e) {
        console.log(e);
      }
    },
    async loadAuthUserPhoto() {
      if (!this.authUser?.id || !this.authUser.avatar) {
        this.authUserPhoto = {};
        return;
      }
      try {
        this.authUserPhoto = await UserService.getUserPhotoById({ id: Number(this.authUser.id) });
      } catch (e) {
        console.log(e);
      }
    },
    onMenuItemSelected(item: MenuItem) {
      switch (item.id) {
        case "discussions":
          this.$router.push(
            Object.assign({ hash: this.isExpert ? "#requiresAction" : "#newOpportunities" }, item.route)
          );
          break;
        case "myLearners":
          this.$router.push({ path: "/assigned-learners" }).catch(() => {
          });
          break;
        case "cancelLoginAs":
          markImpersonationSwitch();
          this.cancelLoginAs();
          break;
        case "signout":
          this.logout();
          break;
        case "contactUs":
          this.showContactUsModal = true;
          break;
        case "taxForms":
          this.$eventBus.$emit("showTaxForms");
          this.$emit("tax-form-open");
        default:
          if (item.route) {
            this.$router.push(item.route).catch(() => {
              /* Silent the error */
            });
          }
      }
    },
    async sendEmail() {
      if (!this.emailBody.trim()) {
        this.$alert("Please enter a message before submitting.");
        return;
      }
      const payload = {
        emailBody: this.emailBody.trim()
      };
      if (this.loading) {
        return; // Prevent multiple submissions
      }
      this.loading = true;

      try {
        const response = await SupportService.createSupportEmail({ requestBody: payload });
        this.$alert("Your message has been sent successfully.");
        this.showContactUsModal = false;
        this.emailBody = "";
      } catch (error) {
        console.error("Error sending email:", error);
        this.$alert("An error occurred while sending your message. Please try again later.");
      } finally {
        this.loading = false;
      }
    },
    openContactUs() {
      this.showContactUsModal = true;
      this.isMenuOpen = false; // optional: closes drawer on mobile if you want
    },
  },
  watch: {
    "user.id": {
      handler() {
        this.loadUserPhoto();
      },
      immediate: true
    },
    "user.avatar"() {
      this.loadUserPhoto();
    },
    "authUser.id": {
      handler() {
        this.loadAuthUserPhoto();
      },
      immediate: true
    },
    "authUser.avatar"() {
      this.loadAuthUserPhoto();
    }
  },
  mounted() {
    this.$eventBus.$on('openContactUs', this.openContactUs);
  },
  beforeDestroy() {
    this.$eventBus.$off('openContactUs', this.openContactUs);
  },
});
</script>

<style lang="scss" scoped>
#contactUs.v-list-item--link {
  &:before,
  & .v-list-item__title {
    background-color: transparent;
    color: #000000;
  }

  &:hover::before {
    background-color: rgba(0, 0, 0, 0.87);
    opacity: 0.04;
  }
}

.user-menu .v-list-item--active {
  color: var(--v-anchor-base);
}

.rounded-lg {
  border-radius: 10px !important;
}

.fixed-size {
  :deep(.v-btn__content) {
    max-width: 700px;
    justify-content: left;
  }

  .text-nowrap {
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 1200px) {
  .fixed-size :deep(.v-btn__content) {
    max-width: 575px;
  }
}

@media (max-width: 1000px) {
  .fixed-size :deep(.v-btn__content) {
    max-width: 450px;
  }
}

@media (max-width: 959px) {
  .fixed-size :deep(.v-btn__content) {
    max-width: 400px;
  }
}

@media (max-width: 844px) {
  .fixed-size :deep(.v-btn__content) {
    max-width: 250px;
  }
}

@media (max-width: 649px) {
  .fixed-size :deep(.v-btn__content) {
    max-width: 175px;
    justify-content: center;
  }
  .fixed-size {
    min-width: 54px !important;
  }
}
</style>
